/** The action primitive. A destructive action uses `variant="destructive"`, and nothing else does. */

import type { CSSProperties, MouseEvent, ReactNode } from "react";

import { cn } from "@/lib/cn";

import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  readonly variant?: ButtonVariant;
  /** sm is 32px, md 40px, lg 48px, off `--al-control-*`. */
  readonly size?: ButtonSize;
  /** Shows a spinner, sets `aria-busy`, and implies disabled. */
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly type?: "button" | "submit" | "reset";
  readonly icon?: ReactNode;
  readonly "aria-label"?: string;
  readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  disabled = false,
  type = "button",
  icon,
  "aria-label": ariaLabel,
  onClick,
  className,
  style,
  children,
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn("al-button", `al-button-${variant}`, `al-button-${size}`, className)}
      style={style}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {loading ? <span className="al-spinner" aria-hidden="true" /> : null}
      {!loading && icon !== undefined ? (
        <span className="al-button-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  );
}
